/**
 * Canonical dashboard widget keys — mirrors backend
 * `zephix-backend/src/modules/dashboards/widgets/widget-allowlist.ts`
 * (Agent 2 recon / DASH-WIDGET-RESILIENCE-1 / dash-load-fail-1.md).
 *
 * Wire field is `widgetKey`. Keys without a live FE component stay registered
 * as `unsupported` so the drift guard fails when BE adds a key FE never saw.
 */

/** Copy for unknown / invalid / not-yet-rendered cards. */
export const UNSUPPORTED_WIDGET_COPY = "This card isn't supported yet";

/**
 * Every backend allowlist key must appear here.
 * - `component` — WidgetRenderer has a real implementation
 * - `unsupported` — intentional placeholder slot (dashboard still loads)
 */
export const WIDGET_RENDER_PLAN = {
  project_health: 'component',
  sprint_metrics: 'unsupported',
  resource_utilization: 'component',
  conflict_trends: 'component',
  budget_variance: 'unsupported',
  risk_summary: 'unsupported',
  portfolio_summary: 'unsupported',
  program_summary: 'unsupported',
  critical_path_risk: 'unsupported',
  earned_value_summary: 'unsupported',
  my_tasks_today: 'unsupported',
  overdue_tasks: 'unsupported',
  blocked_tasks: 'unsupported',
  tasks_by_status: 'unsupported',
  projects_at_risk: 'unsupported',
  upcoming_deadlines: 'unsupported',
  milestone_progress: 'unsupported',
  workload_distribution: 'unsupported',
  resource_capacity: 'unsupported',
  active_risks: 'unsupported',
} as const;

export type WidgetKey = keyof typeof WIDGET_RENDER_PLAN;

/** Ordered list matching BE WIDGET_ALLOWLIST (mutation authority). */
export const WIDGET_ALLOWLIST = Object.keys(WIDGET_RENDER_PLAN) as WidgetKey[];

/** FE-only legacy surfaces (not in BE mutation allowlist). */
const LEGACY_RENDERABLE = new Set(['note', 'table', 'chart']);

export function isWidgetKeyAllowed(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(WIDGET_RENDER_PLAN, key);
}

/** True when WidgetRenderer will paint a real card (not the unsupported placeholder). */
export function hasLiveWidgetRenderer(widgetKey: string): boolean {
  if (LEGACY_RENDERABLE.has(widgetKey)) return true;
  return (
    isWidgetKeyAllowed(widgetKey) &&
    WIDGET_RENDER_PLAN[widgetKey as WidgetKey] === 'component'
  );
}
